import { Controller } from '@hotwired/stimulus';
import * as Turbo from '@hotwired/turbo';

const DEFAULT_DELAY_MS = 600;
const SAVED_HIDE_MS = 2000;

export default class extends Controller {
  static targets = ['status'];

  static values = {
    delay: { type: Number, default: DEFAULT_DELAY_MS }
  };

  static instances = new Set();
  static leaveGuardBound = false;

  connect() {
    this.timer = null;
    this.fadeTimer = null;
    this.inFlight = null;
    this.snapshot = this.serialize();

    this.constructor.instances.add(this);
    this.constructor.ensureLeaveGuards();
  }

  disconnect() {
    this.clearTimer();
    this.clearFadeTimer();
    if (this.isDirty()) {
      this.saveKeepalive();
    }
    this.constructor.instances.delete(this);
    this.constructor.removeLeaveGuards();
  }

  static ensureLeaveGuards() {
    if (this.leaveGuardBound) return;

    this.onBeforeUnload = () => {
      for (const instance of this.instances) {
        instance.flushKeepalive();
      }
    };

    this.onBeforeVisit = async (event) => {
      if (!this.hasPendingChanges()) return;

      event.preventDefault();
      const saved = await this.flushAll();
      if (!saved) {
        window.alert('Could not save your changes. Fix any errors and try again.');
        return;
      }

      Turbo.visit(event.detail.url);
    };

    window.addEventListener('beforeunload', this.onBeforeUnload);
    document.addEventListener('turbo:before-visit', this.onBeforeVisit);
    this.leaveGuardBound = true;
  }

  static removeLeaveGuards() {
    if (this.instances.size > 0 || !this.leaveGuardBound) return;

    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('turbo:before-visit', this.onBeforeVisit);
    this.leaveGuardBound = false;
  }

  static hasPendingChanges() {
    for (const instance of this.instances) {
      if (instance.hasPendingChanges()) return true;
    }
    return false;
  }

  static async flushAll() {
    const instances = [...this.instances];
    const results = await Promise.allSettled(instances.map((instance) => instance.flush()));

    return results.every((result) => result.status === 'fulfilled' && result.value !== false);
  }

  schedule() {
    this.clearTimer();
    this.timer = window.setTimeout(() => this.save(), this.delayValue);
  }

  async flush() {
    this.clearTimer();

    if (this.inFlight) {
      try {
        await this.inFlight;
      } catch {
        return false;
      }
    }

    if (!this.isDirty()) return true;

    try {
      await this.save();
      return !this.isDirty();
    } catch {
      return false;
    }
  }

  flushKeepalive() {
    this.clearTimer();
    if (!this.isDirty()) return;

    this.saveKeepalive();
  }

  saveKeepalive() {
    fetch(this.element.action, {
      method: 'POST',
      body: new FormData(this.element),
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    });
  }

  async save() {
    this.clearTimer();
    if (!this.isDirty()) return;

    if (this.inFlight) {
      await this.inFlight.catch(() => {});
      if (this.isDirty()) return this.save();
      return;
    }

    this.showStatus('saving');

    this.inFlight = fetch(this.element.action, {
      method: 'POST',
      body: new FormData(this.element),
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const message = Array.isArray(data.errors) ? data.errors.join(', ') : data.error;
          throw new Error(message || 'Could not save');
        }
        this.snapshot = this.serialize();
        this.showStatus('saved');
      })
      .catch((error) => {
        this.showStatus('error', error.message);
        throw error;
      })
      .finally(() => {
        this.inFlight = null;
      });

    await this.inFlight;
  }

  hasPendingChanges() {
    return this.isDirty() || this.timer !== null || this.inFlight !== null;
  }

  isDirty() {
    return this.serialize() !== this.snapshot;
  }

  serialize() {
    return new URLSearchParams(new FormData(this.element)).toString();
  }

  showStatus(state, message = '') {
    if (!this.hasStatusTarget) return;

    const el = this.statusTarget;
    this.clearFadeTimer();

    const labels = {
      saving: 'Saving…',
      saved: 'Saved',
      error: message || 'Could not save'
    };

    el.textContent = labels[state] || '';
    el.hidden = false;
    el.dataset.state = state;

    if (state === 'saved') {
      this.fadeTimer = window.setTimeout(() => {
        el.hidden = true;
        el.textContent = '';
        delete el.dataset.state;
      }, SAVED_HIDE_MS);
    }
  }

  clearTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  clearFadeTimer() {
    if (this.fadeTimer) {
      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
